import React from "react";
import BUSINESS from "../config/business";

export default function About() {
  return (
    <section className="section about" id="about" aria-labelledby="about-title">
      <div className="wrap about-grid">
        <div>
          <span className="eyebrow">Meet your carpenter</span>
          <h2 id="about-title">Experienced hands.<br />A local approach.</h2>
          <p className="signature">Jason Murray</p>
          <span className="owner-line">Owner &bull; Wells, Maine</span>
        </div>
        <div>
          <p>Jason brings extensive hands-on carpentry and construction experience across fencing, decks, sheds, doors, windows, finish woodworking, painting, core drilling, and concrete slabs. Small remodels and combined project lists are welcome for homeowners.</p>
          <p>JKM Coastal Carpentry &amp; Construction is built around a simple idea: quality work, agreed in writing, delivered on schedule. <strong>Owner-operated LLC, fully insured ($1M/$2M general liability).</strong> Homeowners work directly with Jason. Regional builders and general contractors get a reliable sub for defined scopes from southern Maine to the New Hampshire seacoast. <strong>Full-house framing and new-home construction are not offered.</strong></p>
          <p className="schedule-note">
            <strong>Thoughtful scheduling, from the start.</strong><br />
            Select weekday appointments and weekend availability by arrangement. Project timing, workdays, and expected duration are discussed before a job is booked.
          </p>
        </div>
      </div>
    </section>
  );
}

export function Process() {
  const steps = [
    { num: "STEP 01", title: "Share the project.", copy: "Tell us your town, what you need done, and your ideal timeframe. A few photos and approximate measurements are a useful starting point." },
    { num: "STEP 02", title: "Review the details.", copy: "Jason reviews the scope and available workdays. A site visit may be needed before a written estimate can be prepared." },
    { num: "STEP 03", title: "Agree on the work.", copy: "Scope, materials, price, and project timing are agreed in writing before work begins. Changes are discussed before proceeding." },
  ];
  return (
    <section className="section" id="process" aria-labelledby="process-title">
      <div className="wrap">
        <span className="eyebrow">A straightforward start</span>
        <h2 id="process-title">From your list<br />to a clear plan.</h2>
        <div className="steps">
          {steps.map((s) => (
            <article className="step" key={s.num}>
              <span className="step-num">{s.num}</span>
              <h3>{s.title}</h3>
              <p>{s.copy}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Area() {
  const towns = [
    "Portland, ME",
    "South Portland",
    "Scarborough",
    "Old Orchard Beach",
    "Saco",
    "Biddeford",
    "Kennebunkport",
    "Kennebunk",
    "Wells",
    "Ogunquit",
    "York",
    "Kittery",
    "Portsmouth, NH",
  ];
  // If a Google Business Profile Place ID is configured, embed the actual business
  // profile (ties the site's map directly to Google reviews & the profile listing).
  // Otherwise, fall back to a keyless coverage-area map of the Portland-Portsmouth corridor.
  const placeId = BUSINESS.googlePlaceId;
  const mapSrc = placeId
    ? `https://www.google.com/maps/embed/v1/place?key=&q=place_id:${placeId}`
    : "https://www.google.com/maps?q=Wells,+Maine&z=9&ll=43.45,-70.55&t=m&output=embed";
  // The v1 embed requires an API key; if none, use the legacy embed with the place query.
  const finalMapSrc = placeId
    ? `https://maps.google.com/maps?q=place_id:${placeId}&hl=en&z=9&output=embed`
    : mapSrc;
  return (
    <section className="area" aria-labelledby="area-title">
      <div className="wrap area-inner">
        <div>
          <span className="eyebrow">Service area</span>
          <h2 id="area-title">Based in Wells.<br />Portland to Portsmouth.</h2>
          <p>Southern Maine coast and the New Hampshire seacoast. Projects considered based on location, scope, and availability.</p>
          <div className="towns" aria-label="Service towns from Portland, ME to Portsmouth, NH" style={{ marginTop: 20 }}>
            {towns.map((t) => <span className="town" key={t}>{t}</span>)}
          </div>
          {BUSINESS.googleProfileUrl && (
            <a
              className="btn outline area-google-btn"
              href={BUSINESS.googleProfileUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginTop: 22 }}
            >
              View on Google <span aria-hidden="true">&rarr;</span>
            </a>
          )}
        </div>
        <div className="area-map" aria-label="Map of the service area from Portland, Maine to Portsmouth, New Hampshire">
          <iframe
            title="Service area map — Portland, ME to Portsmouth, NH"
            src={finalMapSrc}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen=""
          />
          <div className="area-map-caption">
            {placeId ? "JKM Coastal Carpentry \u2022 Wells, ME" : "Portland, ME \u2192 Portsmouth, NH"}
          </div>
        </div>
      </div>
    </section>
  );
}

export function Faq() {
  const items = [
    { q: "Are you licensed and insured?", a: "Yes. JKM Coastal Carpentry LLC is a Maine-registered limited liability company and carries commercial general liability insurance at $1,000,000 per occurrence / $2,000,000 aggregate through Spinnaker Insurance Company. A Certificate of Insurance is available on request, and additional-insured endorsements are welcome for general contractors and property-owner contracts." },
    { q: "Do you take on smaller jobs?", a: "Yes. Smaller carpentry and home-improvement projects are the focus. A door replacement, trim installation, fence repair, or a list of related tasks is a good reason to get in touch." },
    { q: "Can you work at a seasonal home?", a: "Seasonal-property projects are welcome for review. Access arrangements, owner communication, and the work schedule are agreed before the job begins. This is not a property-watch or emergency-response service." },
    { q: "Are weekend appointments available?", a: "Weekend availability is by arrangement. Jason also schedules selected weekdays. Availability depends on the project and existing commitments; workdays are confirmed before booking." },
    { q: "What about additions or larger renovations?", a: "Small remodels and defined project lists are a good fit. Full-house framing, new-home construction, and whole-structure builds are not offered. Larger renovations and additions need an individual review of scope, permits, scheduling, and any specialist trades before a fit can be confirmed." },
    { q: "Can I get a price from photos?", a: "Photos help with the initial conversation, but hidden conditions, measurements, and installation details may require a site visit before a written estimate. An inquiry is not a booking or a binding quote." },
  ];
  return (
    <section className="section faq" aria-labelledby="faq-title">
      <div className="wrap faq-grid">
        <div>
          <span className="eyebrow">A few useful answers</span>
          <h2 id="faq-title">Before we<br />get started.</h2>
        </div>
        <div>
          {items.map((it) => (
            <details key={it.q}>
              <summary>{it.q}</summary>
              <p>{it.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
